import React, { useEffect } from 'react';
import {
  ButtonBar,
  CheckboxField,
  ChecklistField,
  PopoverDialog,
  PrimaryButton,
  RadioField,
  SecondaryButton,
  Text,
  UsePopoverDialogResponse,
  useForm,
} from '@weave/design-system';
import { theme } from '@weave/theme-original';
import { css } from '@emotion/core';
import { AddOn } from '@weave/schema-gen-ts/dist/schemas/package-service/v1/package.pb';
import {
  BundlePackagesEnum,
  IntegrationPackagesEnum,
  SalesforceAccountProductBundle,
} from '../customization-page-v2.types';
import { TableFilterState, useCustomizationTableFilter } from '../providers';
import {
  BundleAddOnsEnum,
  bundleAddOnOptions,
  bundlePackageOptions,
  computeNewBundle,
  computeNewBundleAddOns,
  computeOldBundle,
  getProductPackageBundleName,
} from '../customization-page.utils';
import { difference, isEqual } from 'lodash';

interface Props {
  popoverProps: UsePopoverDialogResponse;
  onApply: (filterValue: SalesforceAccountProductBundle | '', addOns?: AddOn[]) => void;
}

export const CustomizationPageTableFilters = ({ popoverProps, onApply }: Props) => {
  const { filters: filterContextValues, updateFilters } = useCustomizationTableFilter();

  const {
    values: formValues,
    getFieldProps,
    seedValues,
    formProps,
  } = useForm({
    fields: {
      // Bundle type toggle: new vs old
      bundleType: {
        type: 'radio',
        value: filterContextValues.bundleType,
      },
      // Integration (Core / Plus)
      integration: {
        type: 'radio',
        value: filterContextValues.integration,
      },
      oldBundlePackage: {
        type: 'radio',
        value: filterContextValues.oldBundlePackage ?? undefined,
      },
      phones: {
        type: 'checkbox',
        value: filterContextValues.phones,
      },
      // New bundle: add-ons checklist (Base always checked, Coordinate + Attract optional)
      addOns: {
        type: 'checklist',
        value: filterContextValues.addOns,
      },
      isSoftwareOnly: {
        type: 'checkbox',
        value: filterContextValues.isSoftwareOnly,
      },
    },
    onSubmit: () => {
      updateFilters(formValues as TableFilterState);
      if (formValues.bundleType === 'new') {
        const integration = formValues.integration as IntegrationPackagesEnum;
        if (!integration) return;
        const bundle = computeNewBundle(integration, formValues.phones);
        const addOns = computeNewBundleAddOns(
          integration,
          formValues.addOns as BundleAddOnsEnum[]
        );
        onApply(bundle, addOns);
      } else {
        const integration = formValues.integration as IntegrationPackagesEnum;
        const bundlePackage = formValues.oldBundlePackage as BundlePackagesEnum;
        if (!integration || !bundlePackage) return;
        const bundle = formValues.isSoftwareOnly
          ? getProductPackageBundleName(integration, bundlePackage, true)
          : computeOldBundle(integration, bundlePackage);
        if (!bundle) return;
        onApply(bundle);
      }
      closePopover();
    },
  });

  const { close: closePopover, getDialogProps } = popoverProps;

  const isApplyDisabled =
    formValues.bundleType === 'new'
      ? !formValues.integration
      : !formValues.integration || !formValues.oldBundlePackage;

  useEffect(() => {
    if (isEqual(formValues, filterContextValues)) {
      return;
    }

    const addOnsDiff = difference(formValues.addOns, filterContextValues.addOns);
    seedValues({
      bundleType: filterContextValues.bundleType,
      integration: filterContextValues.integration,
      phones: filterContextValues.phones,
      addOns: addOnsDiff,
      oldBundlePackage: filterContextValues.oldBundlePackage,
      isSoftwareOnly: filterContextValues.isSoftwareOnly,
    });
  }, [filterContextValues]);

  return (
    <PopoverDialog {...getDialogProps()} css={{ minWidth: 364 }}>
      <form {...formProps}>
        <Text size="large" weight="bold" css={styles.sectionHeader}>
          Bundle Type
        </Text>
        <RadioField
          {...getFieldProps('bundleType')}
          label=""
          css={[styles.field, styles.horizontalRow]}
        >
          <RadioField.Radio value="new">New</RadioField.Radio>
          <RadioField.Radio value="old">Old</RadioField.Radio>
        </RadioField>

        <Text size="large" weight="bold" css={styles.sectionHeader}>
          Select Integration
        </Text>
        <RadioField
          {...getFieldProps('integration')}
          label=""
          css={[styles.field, styles.horizontalRow]}
        >
          <RadioField.Radio value={IntegrationPackagesEnum.CORE}>Core</RadioField.Radio>
          <RadioField.Radio value={IntegrationPackagesEnum.PLUS}>Plus</RadioField.Radio>
        </RadioField>

        {formValues.bundleType === 'new' && (
          <>
            <Text size="large" weight="bold" css={styles.sectionHeader}>
              Select Bundles
            </Text>
            <ChecklistField {...getFieldProps('addOns')} label="" css={styles.field}>
              {bundleAddOnOptions.map(({ value, label }) => (
                <ChecklistField.Option
                  key={value}
                  name={value}
                  disabled={value === BundleAddOnsEnum.BASE}
                >
                  {label}
                </ChecklistField.Option>
              ))}
            </ChecklistField>

            <div css={{ marginTop: theme.spacing(4) }}>
              <CheckboxField
                {...getFieldProps('phones')}
                label="Phones"
                css={styles.field}
              />
            </div>
          </>
        )}

        {formValues.bundleType === 'old' && (
          <>
            <Text size="large" weight="bold" css={styles.sectionHeader}>
              Select Bundle
            </Text>
            <RadioField
              {...getFieldProps('oldBundlePackage')}
              label=""
              css={styles.field}
            >
              {bundlePackageOptions.map(({ value, label }) => (
                <RadioField.Radio key={value} value={value}>
                  {label}
                </RadioField.Radio>
              ))}
            </RadioField>

            <div css={{ marginTop: theme.spacing(4) }}>
              <CheckboxField
                {...getFieldProps('isSoftwareOnly')}
                label="Software Only"
                css={styles.field}
              />
            </div>
          </>
        )}

        <ButtonBar>
          <SecondaryButton type="button" onClick={closePopover}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={isApplyDisabled}>
            Apply
          </PrimaryButton>
        </ButtonBar>
      </form>
    </PopoverDialog>
  );
};

const styles = {
  field: css`
    padding: ${theme.spacing(0, 2)};
  `,
  horizontalRow: css`
    display: flex;
    gap: ${theme.spacing(3)};
  `,
  sectionHeader: css`
    margin: ${theme.spacing(2, 0, 1)};
    padding-left: ${theme.spacing(2)};
  `,
};
